/*
 * decaffeinate suggestions:
 * DS102: Remove unnecessary code created because of implicit returns
 * DS206: Consider reworking classes to avoid initClass
 * DS207: Consider shorter variations of null checks
 * Full docs: https://github.com/decaffeinate/decaffeinate/blob/master/docs/suggestions.md
 */
import * as d3 from 'd3';
import {ScaleLinear} from 'd3';
import _ from 'underscore';

import {TraceData} from 'analyses/Analyses';
import BenchlingConfig from 'BenchlingConfig';
import {Sequence} from 'components/Sequence';
import {DETAILED_NUCLEOTIDE_SEQUENCE_STATIC_ZOOM} from 'constants/MapsConstants';
import ResiduesView, {DNASliceDatum, ResiduesViewOptions} from 'molbio/maps/linear/ResiduesView';
import * as vo from 'settings/visibility-options/VisibilityOptions';
import {VisibilityOption} from 'settings/visibility-options/VisibilityOptions';
import addTestAttrsToDOMElement from 'test/lib/cypress/addTestAttrsToDOMElement';
import {ElementMouseEvent} from 'util/Types';

import {DNA} from './LinearLayout';

interface DNAViewOptions extends ResiduesViewOptions {
  shouldShowMods: boolean;
  sequence: Sequence;
  traceData?: TraceData;
}

class DNAView extends ResiduesView {
  layout!: DNA;
  shouldShowMods!: boolean;
  sequence!: Sequence;
  traceData: TraceData | undefined;

  static initClass(): void {
    this.prototype.className = 'dna residues';
    this.prototype.setting = vo.DNA.name;
  }

  bottomMarginBleed(): number {
    // For Type.largest (24px), 4px was observed as the descent below the baseline.
    return 4;
  }

  initialize(options: DNAViewOptions): void {
    super.initialize(options);
    this.layout = this.model.get('layout').DNA;
    this.sequence = this.model.get('sequence');
    this.traceData = options.traceData;
    this.layout.set({complement: this.shouldShowComplement()});
    const isRNASeqWithVisibleMods =
      this.sequence.isRNASequence() && this.model.get('zoom') === DETAILED_NUCLEOTIDE_SEQUENCE_STATIC_ZOOM;
    this.layout.set({
      detailedView: this.sequence.isOligo() || isRNASeqWithVisibleMods,
    });
  }

  complementOption(): VisibilityOption | undefined {
    return this.model.get('visibilityOptions').getOption(vo.COMPLEMENT.name);
  }

  baseQualityCasingOption(): VisibilityOption | undefined {
    return this.model.get('visibilityOptions').getOption(vo.ALIGNMENT_BASE_QUALITY_CAPITALIZATION.name);
  }

  shouldShow(): boolean {
    return this.option!.get('visible');
  }

  shouldShowComplement(): boolean {
    const complementOption = this.complementOption();
    return complementOption != null && complementOption.get('visible');
  }

  showComplement = (): void => {
    this.layout.set({complement: this.shouldShowComplement()});
    this.update(true);
  };

  registerListeners(): void {
    super.registerListeners();
    this.listenTo(this.complementOption(), 'change:visible', this.showComplement);
    this.listenTo(this.model.get('sequence').get('_sequence'), 'change:type', this.showComplement);
    const baseQualityOption = this.baseQualityCasingOption();
    if (baseQualityOption != null) {
      this.listenTo(baseQualityOption, 'change:visible', () => {
        this.update(true);
      });
    }
  }

  die(): void {
    super.die();
    d3.select(this.el).select('g').selectAll('rect').on('.dnaview', null);
    d3.select(this.el).select('g').selectAll('text').on('.dnaview', null);
  }

  getText(d: DNASliceDatum): string {
    if (d.visible) {
      return d.bases;
    } else {
      return '';
    }
  }

  getStartOffset(d: DNASliceDatum): number {
    return 0;
  }

  getBasesData(): [DNASliceDatum, DNASliceDatum] {
    const basesCasedByQuality = (bases: string, qualityScores: Array<number | null>): string =>
      qualityScores
        .map((score: number | null, i: number): string =>
          score && score >= BenchlingConfig.MOLBIO_ALIGNMENT_CAPITALIZATION_QUALITY_THRESHOLD
            ? bases[i].toUpperCase()
            : bases[i].toLowerCase()
        )
        .join('');

    const [fwdSlice, revSlice] = this.sequence.get('dna').slice(this.slice.start(), this.slice.end());
    const sliceData: [DNASliceDatum, DNASliceDatum] = [
      {...fwdSlice, visible: true},
      {...revSlice, visible: this.shouldShowComplement()},
    ];
    const qualityBasedCasingOptionEnabled = this.baseQualityCasingOption()?.get('visible');
    const canApplyQualityBasedCapitalization =
      qualityBasedCasingOptionEnabled &&
      this.traceData != null &&
      this.traceData.get('qualityScores').length > 0;
    if (canApplyQualityBasedCapitalization) {
      const slicedQualityScores = this.traceData!.get('qualityScores').slice(
        this.slice.start(),
        this.slice.end()
      );
      return [
        {...sliceData[0], bases: basesCasedByQuality(sliceData[0].bases, slicedQualityScores)},
        {...sliceData[1], bases: basesCasedByQuality(sliceData[1].bases, slicedQualityScores)},
      ];
    } else if (qualityBasedCasingOptionEnabled) {
      return [
        {...sliceData[0], bases: sliceData[0].bases.toUpperCase()},
        {...sliceData[1], bases: sliceData[1].bases.toUpperCase()},
      ];
    }
    return sliceData;
  }

  update(trigger: boolean): void {
    if (!this.shouldShow()) {
      return;
    }

    const rectData = ['test'];
    const data: [DNASliceDatum, DNASliceDatum] | [] = this.layout.get('type').manual
      ? []
      : this.getBasesData();
    const rectRoot = d3.select(this.el).select('g').selectAll('rect').data(rectData);
    rectRoot.exit().remove();
    rectRoot.enter().append('svg:rect');

    let x: ScaleLinear<number, number> | number = this.slice.x();

    const width = x(this.slice.end()) - x(0);

    const contextMenuHandler = (e: ElementMouseEvent): void => {
      if (e.altKey && window.debug) {
        return;
      }
      const offset = e.pageX - this.$el.offset()!.left;
      x = Math.round(this.slice.xInv()(offset));
      const selection = this.model.get('sequence').get('selection');
      selection.reset(x);
    };

    rectRoot
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', this.layout.innerHeight())
      .classed('container-hidden', true);

    const parent = d3.select(this.el).select('g');
    // eslint-disable-next-line no-restricted-syntax
    parent.on('contextmenu.dnaview', _.bind(contextMenuHandler, this));
    // Allows hover events from the modified nucleotides slice to go through in oligos
    if (this.layout.get('detailedView')) {
      d3.select(this.el).style('pointer-events', 'none');
    }

    // Add in DNA text
    this.appendTextRows(parent, data);
    if (trigger) {
      this.trigger('redraw');
    }

    addTestAttrsToDOMElement(this.el, {component: 'SliceView', element: 'dna'});
  }
}
DNAView.initClass();

export default DNAView;
